import axios, { type AxiosError, type AxiosResponse } from "axios";
import { env } from "@/env";

// Extend the AxiosRequestConfig interface to include our retry flag
declare module "axios" {
	interface InternalAxiosRequestConfig {
		_retry?: boolean;
	}
}

const api = axios.create({
	baseURL: env.VITE_BACKEND_API_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Store the getToken function globally so interceptors can access it
let globalGetToken: (() => Promise<string | null>) | null = null;

export const setGlobalGetToken = (getToken: () => Promise<string | null>) => {
	globalGetToken = getToken;
};

// Request interceptor to attach Authorization header proactively
api.interceptors.request.use(async (config) => {
	try {
		if (globalGetToken) {
			const token = await globalGetToken();
			if (token) {
				config.headers = config.headers || {};
				config.headers.Authorization = `Bearer ${token}`;
			}
		}
	} catch (_err) {
		// If token retrieval fails, proceed without token; response interceptor will handle 401
	}
	return config;
});

// Response interceptor to handle token refresh on 401 errors
api.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config;

		// Check if error is 401 and we haven't already retried
		if (
			error.response?.status === 401 &&
			originalRequest &&
			!originalRequest._retry &&
			globalGetToken
		) {
			originalRequest._retry = true;

			try {
				// Get a fresh token
				const newToken = await globalGetToken();

				if (newToken && originalRequest.headers) {
					// Update the authorization header with the new token
					originalRequest.headers.Authorization = `Bearer ${newToken}`;

					// Retry the original request
					return api(originalRequest);
				}
			} catch (refreshError) {
				console.error("Token refresh failed:", refreshError);
				// If token refresh fails, redirect to login or handle appropriately
				// For now, we'll just let the original error propagate
			}
		}

		return Promise.reject(error);
	},
);

export default api;
